import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC, ReactNode } from 'react';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import type { ActionFormFieldData } from 'src/pages/actions/update/forms/actionsSchema';
import { defaultValues } from 'src/pages/actions/update/forms/actionsSchema';
import { getContributors, getOwners } from 'src/rbac/contributorHelper';
import type { ObjectWithContributors } from 'src/rbac/Permission';

import { useInsertChildAction } from '@/hooks/mutations/action/useInsertChildAction';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useGetActionById } from '@/hooks/queries';
import { createWithFiles } from '@/utils/createWithFiles';
import { actionDetailsUrl } from '@/utils/urls';

import ActionForm from '../../actions/update/forms/ActionForm';
import type { AssessmentTypeEnum } from '../types';
import { useAssessmentTypeConfig } from '../useAssessmentTypeConfig';

type Props = {
  assessmentMode: AssessmentTypeEnum;
  readonly: boolean;
  assessmentId: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  header?: string;
};

const ConnectedActionForm: FC<Props> = ({
  assessmentMode,
  readonly,
  assessmentId,
  id,
  onDismiss,
  beforeFieldsSlot,
  header,
}) => {
  const { data } = useGetActionById({
    queryArgs: { id: id! },
    shouldSkip: !id,
  });
  const action = data?.action?.[0];
  const {
    routing: { resultsRegisterUrl },
  } = useAssessmentTypeConfig(assessmentMode);

  const { updateFiles } = useFileUpdate();
  const { insertChildAction } = useInsertChildAction();

  const values: ActionFormFieldData = {
    ...defaultValues,
    ...action,
    files: action?.files.map((rf) => rf.file) ?? [],
    Owners: getOwners(action),
    Contributors: getContributors(action),
    ancestorContributors: action?.ancestorContributors ?? [],
  };

  const onSave = async (data: ActionFormFieldData) => {
    const { files } = data;
    const { createdEntity } = await createWithFiles({
      create: () =>
        insertChildAction({
          ...data,
          ParentId: assessmentId,
          CustomAttributeData: data?.CustomAttributeData || undefined,
          DepartmentTypeIds:
            data.departments?.map((d) => d.DepartmentTypeId) || [],
          TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
          ...ownerContributorAndViewerIds(data),
        }),
      getCreatedEntity: (result) => {
        const actionId = result?.insertChildAction?.Id;
        if (!actionId) {
          throw new Error('Action id is missing');
        }

        return { id: actionId, destination: actionDetailsUrl(actionId) };
      },
      saveFiles: ({ id }) =>
        updateFiles({
          parentId: id,
          parentType: Parent_Type_Enum.Action,
          selectedFiles: files,
          originalFiles: values?.files ?? [],
        }),
    });

    return {
      savedIds: [createdEntity.id],
      createdEntity,
      successNavigation: { destination: resultsRegisterUrl(assessmentId) },
    };
  };

  return (
    <ActionForm
      header={header}
      onDismiss={onDismiss}
      values={values}
      onSave={onSave}
      readOnly={readonly}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
      beforeFieldsSlot={beforeFieldsSlot}
    />
  );
};

export default ConnectedActionForm;
